import api from './api';

/** Stati di un Pagamento. Rispecchiano `payments/domain.py`: se ne aggiungi
 *  uno lì, va aggiunto anche qui — non ci sono altri valori possibili. */
export type StatoPagamento =
  | 'AVVIATO'
  | 'PAGATO'
  | 'FALLITO'
  | 'ABBANDONATO'
  | 'RIMBORSATO';

export const STATI_CONCLUSI_OK: StatoPagamento[] = ['PAGATO'];
export const STATI_CONCLUSI_KO: StatoPagamento[] = ['FALLITO', 'ABBANDONATO', 'RIMBORSATO'];

export interface AvvioPagamento {
  /** URL del form da inoltrare: è il dominio Nexi, non il nostro. */
  action: string;
  fields: Record<string, string>;
  codTrans: string;
  /** Importo realmente addebitato: `et_totdoc` del gestionale, che include
   *  le spese di incasso. Può essere maggiore del totale del carrello. */
  importoCentesimi: number;
  /** true quando si è ripreso un pagamento già aperto invece di crearne uno
   *  nuovo — e quindi senza un secondo ordine sul gestionale. */
  ripreso: boolean;
}

/** Pagamento con carta che il Cliente può ancora completare: entro 15
 *  minuti dalla creazione, sullo stesso ordine (`GET /in-sospeso`). */
export interface PagamentoInSospeso {
  codTrans: string;
  numeroOrdine?: string | null;
  /** Il numero del gestionale (`OW/2026/00048`), quello di "I miei ordini"
   *  (INT-134); `numeroOrdine` è il riferimento web. */
  numeroGestionale?: string | null;
  idOrdine?: number | null;
  amountCents: number;
  fingerprint?: string | null;
  /** ISO: dopo quest'ora l'ordine viene annullato e si riparte da capo. */
  scadeAlle: string;
}

export interface Pagamento {
  codTrans: string;
  stato: StatoPagamento;
  amountCents: number;
  divisa: string;
  numeroOrdine?: string | null;
  /** Il numero del gestionale (`OW/2026/00048`), quello di "I miei ordini"
   *  (INT-134); `numeroOrdine` è il riferimento web. */
  numeroGestionale?: string | null;
  idOrdine?: number | null;
  codiceEsito?: string | null;
  codAut?: string | null;
  messaggio?: string | null;
  brand?: string | null;
  pan?: string | null;
  paidAt?: string | null;
  createdAt?: string | null;
}

/** Messaggi per il Cliente a partire da `codiceEsito`.
 *  Tabella in specifiche XPay v20.4, p. 887-889. */
const MESSAGGI: Record<string, string> = {
  '116': 'Autenticazione 3D Secure annullata. Puoi riprovare.',
  '118': 'Autenticazione della banca non riuscita. Riprova o usa un’altra carta.',
  '120': 'Circuito non accettato per questo pagamento.',
  '121': 'Sessione di pagamento scaduta. Riprova.',
  '122': 'Numero massimo di tentativi raggiunto per questo ordine.',
  '400': 'Pagamento non autorizzato. Controlla i dati o contatta la tua banca.',
  '401': 'Carta scaduta o data di scadenza errata.',
  '402': 'Carta non valida o chiusa. Usa un altro metodo di pagamento.',
  '404': 'Operazione non consentita dalla tua banca per questa carta.',
  '405': 'Fondi insufficienti.',
  '406': 'Problema tecnico sui sistemi della banca. Riprova fra poco.',
  '407': 'Banca emittente non raggiungibile. Riprova o usa un’altra carta.',
  '408': 'La banca richiede una verifica aggiuntiva: riprova completando l’autenticazione.',
  '409': 'Operazione bloccata dalla banca per sospetta frode.',
  '410': 'Troppi tentativi di PIN. Contatta la tua banca.',
  '411': 'Contatta la tua banca per sbloccare l’operazione.',
  '412': 'Carta scaduta, smarrita o bloccata.',
  '413': 'Troppi tentativi con la stessa carta: contatta la tua banca.',
  '414': 'Limite di spesa giornaliero superato.',
};

export function messaggioEsito(pagamento: Pagamento): string {
  const perCodice = MESSAGGI[String(pagamento.codiceEsito ?? '')];
  if (perCodice) return perCodice;
  if (pagamento.stato === 'ABBANDONATO') return 'Pagamento non completato.';
  if (pagamento.stato === 'RIMBORSATO') return 'Questo pagamento è stato rimborsato.';
  return pagamento.messaggio || 'Pagamento non autorizzato. Riprova o usa un altro metodo.';
}

export const nexiService = {
  async avvia(orderPayload: unknown, amountEuro: number, descrizione?: string): Promise<AvvioPagamento> {
    const { data } = await api.post<AvvioPagamento>('/api/pagamenti/nexi/avvia', {
      orderPayload,
      amountEuro,
      descrizione,
    });
    return data;
  },

  async inSospeso(): Promise<PagamentoInSospeso | null> {
    const { data } = await api.get<{ pagamento: PagamentoInSospeso | null }>('/api/pagamenti/nexi/in-sospeso');
    return data.pagamento;
  },

  /** Rimanda in cassa lo stesso ordine, senza crearne un altro. */
  async riprendi(codTrans: string): Promise<AvvioPagamento> {
    const { data } = await api.post<AvvioPagamento>(`/api/pagamenti/nexi/riprendi/${codTrans}`);
    return data;
  },

  async stato(codTrans: string): Promise<Pagamento> {
    const { data } = await api.get<Pagamento>(`/api/pagamenti/nexi/stato/${codTrans}`);
    return data;
  },
};
